// API service for DO (Designated Officer) applications management
// Handles all API calls related to applications processing

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class DoApiService {
  // Helper method to handle API responses
  static async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Helper method to get auth headers (if needed in future)
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Get applications list with filtering and pagination
  static async getApplicationsList({
    buttonType = 'btnApplicantLab',
    orgId,
    userTypeId,
    page = 1,
    limit = 50
  }) {
    try {
      const params = new URLSearchParams({
        buttonType,
        orgId: orgId.toString(),
        userTypeId: userTypeId.toString(),
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await fetch(`${API_BASE_URL}/do/applications?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getApplicationsList error:', error);
      throw error;
    }
  }


  static async getMomCompositoryList({
    orgId,
    userTypeId,
    acType = '0',
    year = '0',
    month = '0',
    page = 1,
    limit = 50
  }) {
    try {

      const params = new URLSearchParams({
        orgId: orgId.toString(),
        userTypeId: userTypeId.toString(),
        acType: acType.toString(),
        year: year.toString(),
        month: month.toString(),
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/momCompository?${params}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      return this.handleResponse(response);

    } catch (error) {

      console.error(
        'DoApiService.getMomCompositoryList error:',
        error
      );

      throw error;

    }
  }

  static async getClauseNoList({ standard, feild, appid, orgid, userType }) {
    try {
      const response = await fetch(`${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetClauseNo?standard=${standard}&labType=${feild}&appid=${appid}&orgid=${orgid}&userType=${userType}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getClauseNoList error:', error);
      throw error;
    }
  }

  // Get applications summary/statistics
  static async getApplicationsSummary({ orgId, userTypeId }) {
    try {
      const params = new URLSearchParams({
        orgId: orgId.toString(),
        userTypeId: userTypeId.toString()
      });

      const response = await fetch(`${API_BASE_URL}/do/applications/summary?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getApplicationsSummary error:', error);
      throw error;
    }
  }

  // Get specific application details
  static async getApplicationDetails({ appId, orgId, userTypeId }) {
    try {
      const params = new URLSearchParams({
        orgId: orgId.toString(),
        userTypeId: userTypeId.toString()
      });

      const response = await fetch(`${API_BASE_URL}/do/applications/${appId}?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getApplicationDetails error:', error);
      throw error;
    }
  }

  // Update application status or stage
  static async updateApplicationStatus({
    appId,
    stage,
    status,
    remarks,
    updatedBy,
    orgId,
    userTypeId
  }) {
    try {
      const response = await fetch(`${API_BASE_URL}/do/applications/${appId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          stage,
          status,
          remarks,
          updatedBy,
          orgId,
          userTypeId
        })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.updateApplicationStatus error:', error);
      throw error;
    }
  }

  // Export applications data
  static async exportApplications({
    buttonType = 'btnApplicantLab',
    orgId,
    userTypeId,
    format = 'json'
  }) {
    try {
      const params = new URLSearchParams({
        buttonType,
        orgId: orgId.toString(),
        userTypeId: userTypeId.toString(),
        format
      });

      const response = await fetch(`${API_BASE_URL}/do/applications/export?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.exportApplications error:', error);
      throw error;
    }
  }

  // Get application process history
  static async getApplicationHistory({ appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/do/applications/${appId}/history`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getApplicationHistory error:', error);
      throw error;
    }
  }

  // Get detailed application history for dedicated history page (mode 34)
  static async getLabApplicationHistory({ appId }) {
    try {

      const response = await fetch(`${API_BASE_URL}/do/applications/${appId}/lab-history`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      const result = await this.handleResponse(response);
      console.log('API response:', result);

      return result;
    } catch (error) {
      console.error('DoApiService.getLabApplicationHistory error:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  }

  // Get view application data (mode 86) - equivalent to .NET rptrApps_DataBind
  static async getViewApplicationData({ orgId }) {
    try {
      console.log('DoApiService.getViewApplicationData called with orgId:', orgId);

      const response = await fetch(`${API_BASE_URL}/do/applications/view-data/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('View application data response status:', response.status);

      const result = await this.handleResponse(response);
      console.log('View application data response:', result);

      return result;
    } catch (error) {
      console.error('DoApiService.getViewApplicationData error:', error);
      throw error;
    }
  }

  // Get CAB details (mode 100) - equivalent to .NET getCABDetailAll method
  static async getCABDetails({ appId }) {
    try {
      console.log('DoApiService.getCABDetails called with appId:', appId);

      const response = await fetch(`${API_BASE_URL}/do/applications/${appId}/cab-details`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('CAB details response status:', response.status);

      const result = await this.handleResponse(response);
      console.log('CAB details response:', result);

      return result;
    } catch (error) {
      console.error('DoApiService.getCABDetails error:', error);
      throw error;
    }
  }

  // Utility method to download file from blob
  static downloadFile(data, filename, mimeType = 'application/json') {
    try {
      const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], {
        type: mimeType
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('DoApiService.downloadFile error:', error);
      throw error;
    }
  }

  // Format application data for export
  static formatApplicationsForExport(applications, buttonType) {
    try {
      const getApplicationTypeName = (assessmentTypeId) => {
        const types = {
          1: 'New Application',
          2: 'Renewal',
          3: 'Extension',
          4: 'Surveillance',
          5: 'Re-assessment',
          6: 'Special',
          7: 'Others'
        };
        return types[assessmentTypeId] || 'Unknown';
      };

      const getDisciplineName = (labTypeId) => {
        const disciplines = {
          1: 'Testing',
          2: 'Calibration',
          7: 'Medical'
        };
        return disciplines[labTypeId] || 'N/A';
      };

      const exportData = {
        exportInfo: {
          exportedAt: new Date().toISOString(),
          buttonType: buttonType,
          buttonTypeDisplayName: this.getButtonTypeDisplayName(buttonType),
          totalRecords: applications.length,
          exportedBy: JSON.parse(localStorage.getItem('userData') || '{}').username || 'Unknown'
        },
        applications: applications.map((app, index) => ({
          'Sr. No.': index + 1,
          'Lab ID': app.appId,
          'Application No': app.applicationNo,
          'User Name': app.loginName || app.contactPerson || 'N/A',
          'Laboratory Name': app.laboratoryName,
          'Standard': app.standard || '2022',
          'Application Type': app.assessmentType || getApplicationTypeName(app.assessmentTypeId),
          'Address': app.address || app.addressLine1 || 'N/A',
          'State': app.state || 'N/A',
          'City': app.city || 'N/A',
          'Status': app.status || app.stage || 'Pending',
          'Status Date': this.formatDate(app.lastUpdated) || this.formatDate(app.applicationDate),
          'Discipline': app.disciplines || getDisciplineName(app.labTypeId),
          'Group': app.groups || 'N/A',
          'Field': app.labType || 'N/A',
          'Application Submitted on': this.formatDate(app.submitDate) || this.formatDate(app.applicationDate),
          'Assessment Start Date': this.formatDate(app.assessmentStart) || 'Not Scheduled',
          'Assessment End Date': this.formatDate(app.assessment) || 'Not Scheduled',
          'Contact Email': app.contactEmail || 'N/A',
          'Contact Mobile': app.contactMobile || 'N/A',
          'Pincode': app.pincode || 'N/A',
          'Organization ID': app.orgId,
          'Assessment Type ID': app.assessmentTypeId,
          'Lab Type ID': app.labTypeId,
          'Application Fee Paid': app.applicationFeePaid ? 'Yes' : 'No',
          'Pre Assessment Fee Paid': app.preAssessmentFeePaid ? 'Yes' : 'No',
          'Main Assessment Fee Paid': app.mainAssessmentFeePaid ? 'Yes' : 'No',
          'Is Parked': app.isParked ? 'Yes' : 'No',
          'Last Updated': app.lastUpdated
        }))
      };

      return exportData;
    } catch (error) {
      console.error('DoApiService.formatApplicationsForExport error:', error);
      throw error;
    }
  }

  // Get user data from localStorage
  static getUserData() {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (error) {
      console.error('DoApiService.getUserData error:', error);
      return {};
    }
  }

  // Validate user session
  static validateUserSession() {
    const userData = this.getUserData();
    return userData.orgId && userData.userTypeId;
  }

  // Format date for display
  static formatDate(dateString, format = 'default') {
    if (!dateString) return 'N/A';

    // Handle array of dates (like submitDate)
    if (Array.isArray(dateString)) {
      dateString = dateString[0]; // Use first element
    }

    let date;

    // Check if the date string is in DD/MM/YYYY format
    if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      // Convert to ISO format (YYYY-MM-DD) for proper parsing
      date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return 'Invalid Date';

    switch (format) {
      case 'short':
        return date.toLocaleDateString('en-IN');
      case 'long':
        return date.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      case 'datetime':
        return date.toLocaleString('en-IN');
      case 'default':
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      default:
        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
    }
  }



  // Get button type display name
  static getButtonTypeDisplayName(buttonType) {
    const displayNames = {
      'btnApplicantLab': 'New Applications',
      'btnAccreditatedLab': 'Accredited Applications',
      'btnAllLab': 'All Applications',
      'btnWorkableFiles': 'Workable Files',
      'btnIlac': 'ILAC Applications'
    };

    return displayNames[buttonType] || buttonType;
  }

  // Get payment types for dropdown (sp_CabProc mode 13)
  static async getPaymentTypes() {
    try {
      console.log('DoApiService.getPaymentTypes called');

      const response = await fetch(
        `${API_BASE_URL}/do/payment-types`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Payment types API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        total: result.total
      });

      return result;
    } catch (error) {
      console.error('Error in getPaymentTypes:', error);
      throw error;
    }
  }

  // Get payment details for application (spAdminReport mode 3)
  static async getPaymentDetails({ appId, paymentTypeId = null, orderId = null }) {
    try {
      console.log('DoApiService.getPaymentDetails called with params:', {
        appId,
        paymentTypeId,
        orderId
      });

      // Build query parameters
      const params = new URLSearchParams({
        appId: appId.toString()
      });

      if (paymentTypeId) {
        params.append('paymentTypeId', paymentTypeId.toString());
      }

      if (orderId) {
        params.append('orderId', orderId);
      }

      const response = await fetch(
        `${API_BASE_URL}/do/payment-details?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Payment details API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        total: result.total,
        filters: result.filters
      });

      return result;
    } catch (error) {
      console.error('Error in getPaymentDetails:', error);
      throw error;
    }
  }

  // Get reports details for application
  static async getReportsDetails({ appId, reportType = null }) {
    try {
      console.log('DoApiService.getReportsDetails called with params:', {
        appId,
        reportType
      });

      // Build query parameters
      const params = new URLSearchParams({
        appId: appId.toString()
      });

      if (reportType) {
        params.append('reportType', reportType);
      }

      const response = await fetch(
        `${API_BASE_URL}/do/reports-details?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Reports details API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        total: result.total,
        filters: result.filters
      });

      return result;
    } catch (error) {
      console.error('Error in getReportsDetails:', error);
      throw error;
    }
  }

  // Get scrutiny reports for application
  static async getScrutinyReports({ appId }) {
    try {
      console.log('DoApiService.getScrutinyReports called with params:', {
        appId
      });

      // Build query parameters
      const params = new URLSearchParams({
        appId: appId.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/scrutiny-reports?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Scrutiny reports API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        total: result.total,
        hasReports: result.hasReports
      });

      return result;
    } catch (error) {
      console.error('Error in getScrutinyReports:', error);
      throw error;
    }
  }

  // Get allotment details for dealing officer and contact person (equivalent to Allotment_Acts stored procedure)
  static async getAllotmentDetails({ mode, appId }) {
    try {
      console.log('DoApiService.getAllotmentDetails called with params:', {
        mode,
        appId
      });

      // Build query parameters
      const params = new URLSearchParams({
        mode: mode.toString(),
        appId: appId.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/allotment-details?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Allotment details API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        mode: mode
      });

      return result;
    } catch (error) {
      console.error('Error in getAllotmentDetails:', error);
      throw error;
    }
  }

  // Get email notifications for application (equivalent to Email_Acts stored procedure mode 2)
  static async getEmailNotifications({ appId, isCab = false }) {
    try {
      console.log('DoApiService.getEmailNotifications called with params:', {
        appId,
        isCab
      });

      // Build query parameters
      const params = new URLSearchParams({
        appId: appId.toString(),
        isCab: isCab.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/email-notifications?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Email notifications API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0
      });

      return result;
    } catch (error) {
      console.error('Error in getEmailNotifications:', error);
      throw error;
    }
  }

  // Get assessor allotment details for Form 8 (equivalent to AssessorAllotment_New stored procedure)
  static async getAssessorAllotmentDetails({ mode, appId }) {
    try {
      console.log('DoApiService.getAssessorAllotmentDetails called with params:', {
        mode,
        appId
      });

      // Build query parameters
      const params = new URLSearchParams({
        mode: mode.toString(),
        appId: appId.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/assessor-allotment?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Assessor allotment API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        mode: mode
      });

      return result;
    } catch (error) {
      console.error('Error in getAssessorAllotmentDetails:', error);
      throw error;
    }
  }
  static async getAssessorAllotmentDetails9({ mode, appId }) {
    try {
      console.log('DoApiService.getAssessorAllotmentDetails9 called with params:', {
        mode,
        appId
      });

      // Build query parameters
      const params = new URLSearchParams({
        mode: mode.toString(),
        appId: appId.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/assessor-allotment9?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Assessor allotment API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
        mode: mode
      });

      return result;
    } catch (error) {
      console.error('Error in getAssessorAllotmentDetails:', error);
      throw error;
    }
  }
  static async getFinalResultDetails({ appId, assessmentId }) {
    try {
      console.log('DoApiService.getFinalResultDetails called with params:', {
        appId,
        assessmentId
      });

      // Build query parameters
      const params = new URLSearchParams({
        appId: appId.toString(),
        assessmentId: assessmentId.toString()
      });

      const response = await fetch(
        `${API_BASE_URL}/do/final-check?${params.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders()
        }
      );

      const result = await this.handleResponse(response);

      console.log('Final result API response:', {
        success: result.success,
        dataCount: result.data ? result.data.length : 0,
      });

      return result;
    } catch (error) {
      console.error('Error in getFinalResultDetails:', error);
      throw error;
    }
  }

  /**
   * Generic POST method for API calls
   * @param {string} endpoint - API endpoint path
   * @param {Object} data - Data to send in request body
   * @returns {Promise<Object>} - API response
   */
  static async genericPost(endpoint, data) {
    try {
      const url = `${API_BASE_URL}${endpoint}`;


      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      const result = await this.handleResponse(response);

      console.log('✅ Generic POST Response:', {
        endpoint,
        success: result.success,
        message: result.message,
        hasData: !!result.data
      });

      return result;
    } catch (error) {
      console.error('❌ Generic POST Error:', {
        endpoint,
        error: error.message,
        stack: error.stack
      });

      return {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  /**
   * Generic GET method for API calls
   * @param {string} endpoint - API endpoint path
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - API response
   */
  static async genericGet(endpoint, params = {}) {
    try {
      const queryParams = new URLSearchParams(params);
      const url = `${API_BASE_URL}${endpoint}?${queryParams}`;

      console.log('🔄 Generic GET Request:', {
        endpoint,
        url,
        params,
        timestamp: new Date().toISOString()
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const result = await this.handleResponse(response);

      console.log('✅ Generic GET Response:', {
        endpoint,
        success: result.success,
        dataCount: Array.isArray(result.data) ? result.data.length : result.data ? 1 : 0
      });

      return result;
    } catch (error) {
      console.error('❌ Generic GET Error:', {
        endpoint,
        error: error.message,
        stack: error.stack
      });

      return {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  /**
   * Generic PUT method for API calls
   * @param {string} endpoint - API endpoint path
   * @param {Object} data - Data to send in request body
   * @returns {Promise<Object>} - API response
   */
  static async genericPut(endpoint, data) {
    try {
      const url = `${API_BASE_URL}${endpoint}`;

      console.log('🔄 Generic PUT Request:', {
        endpoint,
        url,
        dataKeys: Object.keys(data || {}),
        timestamp: new Date().toISOString()
      });

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      const result = await this.handleResponse(response);

      console.log('✅ Generic PUT Response:', {
        endpoint,
        success: result.success,
        message: result.message
      });

      return result;
    } catch (error) {
      console.error('❌ Generic PUT Error:', {
        endpoint,
        error: error.message,
        stack: error.stack
      });

      return {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  /**
   * Generic DELETE method for API calls
   * @param {string} endpoint - API endpoint path
   * @param {Object} data - Optional data to send in request body
   * @returns {Promise<Object>} - API response
   */
  static async genericDelete(endpoint, data = null) {
    try {
      const url = `${API_BASE_URL}${endpoint}`;

      console.log('🔄 Generic DELETE Request:', {
        endpoint,
        url,
        hasData: !!data,
        timestamp: new Date().toISOString()
      });

      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        ...(data && { body: JSON.stringify(data) })
      });

      const result = await this.handleResponse(response);

      console.log('✅ Generic DELETE Response:', {
        endpoint,
        success: result.success,
        message: result.message
      });

      return result;
    } catch (error) {
      console.error('❌ Generic DELETE Error:', {
        endpoint,
        error: error.message,
        stack: error.stack
      });

      return {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  // Get close remarks for application
  static async getCloseRemarks({ appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/jd/closeRemarks?appId=${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.getCloseRemarks error:', error);
      throw error;
    }
  }

  // Get rejected remarks for application
  static async getRejectedRemarks({ appId }) {
    try {
      console.log('DoApiService.getRejectedRemarks called with appId:', appId);

      const response = await fetch(`${API_BASE_URL}/do/rejected-remarks?appId=${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('Rejected remarks response status:', response.status);

      const result = await this.handleResponse(response);
      console.log('Rejected remarks response:', result);

      return result;
    } catch (error) {
      console.error('DoApiService.getRejectedRemarks error:', error);
      throw error;
    }
  }

  // Validate username/lab-id for Collection Center (spAdminReport mode 56)
  static async validateUsername(username, orgId) {
    try {
      const params = new URLSearchParams({
        username,
        orgId: orgId.toString()
      });
      const response = await fetch(`${API_BASE_URL}/collection-center/validate-username?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.validateUsername error:', error);
      throw error;
    }
  }

  // Save collection center with optional file upload (spAdminReport mode 49)
  static async saveCollectionCenter(formData) {
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/collection-center/save`, {
        method: 'POST',
        headers,
        body: formData
      });
      return this.handleResponse(response);
    } catch (error) {
      console.error('DoApiService.saveCollectionCenter error:', error);
      throw error;
    }
  }

  static async markInactive(data) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/do/inActive`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data)
        }
      );

      return await this.handleResponse(response);

    } catch (error) {
      console.error("DoApiService.markInactive error:", error);

      return {
        success: false,
        message: error.message || "Failed to mark inactive"
      };
    }
  }

  static async extendInactiveDays(data) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/do/extendInactive`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data)
        }
      );

      return await this.handleResponse(response);

    } catch (error) {
      console.error("DoApiService.markInactive error:", error);

      return {
        success: false,
        message: error.message || "Failed to mark inactive"
      };
    }
  }

  static async getinactiveremark({ appId }) {
    try {
      console.log('DoApiService.Inactive called with appId:', appId);

      const response = await fetch(`${API_BASE_URL}/do/inactivelist?appId=${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      console.log('Inactive remarks response status:', response.status);

      const result = await this.handleResponse(response);
      console.log('Inactive remarks response:', result);

      return result;
    } catch (error) {
      console.error('DoApiService.Inactive error:', error);
      throw error;
    }
  }

}

export default DoApiService;